import { ActivityLogTab, DocumentsTab } from '@/domains/jobs/components/ActivityLogDocumentsTabs';
import { DFETab } from '@/domains/jobs/components/DFETab';
import { DataCaptureTab } from '@/domains/jobs/components/DataCaptureTab';
import { queryKeys } from '@/lib/query-client';
import { PageHeader } from '@/shared/components/PageHeader';
import { useQueryClient } from '@tanstack/react-query';
import { Briefcase, Camera, FileText, History, Layers, MessageSquare } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useJobData } from '../hooks/useJobData';
import { useJobDocuments } from '../hooks/useJobDocuments';
import { ConversationThread } from './ConversationThread';
import { CustomerInfoCard } from './CustomerInfoCard';
import { JobDetailsCard } from './JobDetailsCard';
import { MapSection } from './MapSection';
import { MilestonesCard } from './MilestonesCard';
import { NoTasksNotice } from './NoTasksNotice';
import { PSRDetailsCard } from './PSRDetailsCard';
import { PermitCard } from './PermitCard';
import { StatusBar } from './StatusBar';
import { TasksCard } from './TasksCard';

interface JobDetailsViewProps {
  jobId: string;
}

export function JobDetailsView({ jobId }: JobDetailsViewProps) {
  const queryClient = useQueryClient();
  const { jobData, jobStatus, conversationThread, isLoading } = useJobData(jobId);
  useJobDocuments(Number(jobId)); // pre-fetch to warm the Documents tab
  const [activeTab, setActiveTab] = useState<
    'overview' | 'activity-log' | 'documents' | 'messages' | 'data-capture' | 'dfe'
  >('overview');
  const [showResponseForm, setShowResponseForm] = useState(false);
  const [responseMessage, setResponseMessage] = useState('');

  const handleSendResponse = () => {
    console.log('Sending response:', responseMessage);
    setResponseMessage('');
    setShowResponseForm(false);
  };

  const hasConversation = conversationThread && conversationThread.length > 0;

  useEffect(() => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.jobs.detail(jobId),
    });
  }, [jobId]);

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#E84A2B] mx-auto mb-4" />
          <p className="text-muted-foreground text-sm">Loading job details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-background overflow-hidden">
      <PageHeader
        title={jobData.title}
        subtitle={`Job #${jobData.id}`}
        icon={<Briefcase className="w-7 h-7 text-orange-600 dark:text-orange-400" />}
      />

      {/* Status Bar */}
      <div className="px-6 pt-6 border-b border-border pb-4">
        <StatusBar jobData={jobData} />
        {/* The detail payload carries the full task array, so derive the flag the
            notice expects — undefined while the payload is still resolving. */}
        <NoTasksNotice
          hasTasks={jobData.tasks === undefined ? undefined : jobData.tasks.length > 0}
          isScheduled={jobData.isScheduled}
        />
      </div>

      {/* Tabs */}
      <div className="border-b border-border">
        <div className="px-6">
          <div className="flex gap-1">
            <button
              onClick={() => {
                setActiveTab('overview');
                queryClient.invalidateQueries({
                  queryKey: queryKeys.jobs.milestoneHistory(jobId),
                });
              }}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'overview'
                  ? 'border-[#E84A2B] text-[#E84A2B]'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <div className="flex items-center gap-2">
                <Layers className="w-4 h-4" />
                Overview
              </div>
            </button>
            <button
              onClick={() => setActiveTab('activity-log')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'activity-log'
                  ? 'border-[#E84A2B] text-[#E84A2B]'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <div className="flex items-center gap-2">
                <History className="w-4 h-4" />
                Activity Log
              </div>
            </button>
            <button
              onClick={() => setActiveTab('documents')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'documents'
                  ? 'border-[#E84A2B] text-[#E84A2B]'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Documents
              </div>
            </button>
            <button
              onClick={() => setActiveTab('messages')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'messages'
                  ? 'border-[#E84A2B] text-[#E84A2B]'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <div className="flex items-center gap-2">
                <MessageSquare className="w-4 h-4" />
                Messages
                {hasConversation && (
                  <span className="ml-1 inline-flex items-center justify-center w-5 h-5 text-xs font-medium rounded-full bg-[#E84A2B] text-white">
                    {conversationThread.length}
                  </span>
                )}
                {jobData.hasResponse && <div className="w-2 h-2 rounded-full bg-green-500" />}
              </div>
            </button>
            <button
              onClick={() => setActiveTab('data-capture')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'data-capture'
                  ? 'border-[#E84A2B] text-[#E84A2B]'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <div className="flex items-center gap-2">
                <Camera className="w-4 h-4" />
                Data Capture
              </div>
            </button>
            {/* TODO: Hidden as per WRK-5460 for first launch */}
            {/* {!jobData.validated && (
              <button
                onClick={() => setActiveTab('dfe')}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'dfe'
                    ? 'border-[#E84A2B] text-[#E84A2B]'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <div className="flex items-center gap-2">
                  <PoundSterling className="w-4 h-4" />
                  DFE
                </div>
              </button>
            )} */}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="px-6 py-6">
          {activeTab === 'overview' && (
            /*
             * Two independently-flowing columns rather than a stack of paired
             * two-column grids (WRK-5792 follow-up). In a grid row every cell
             * stretches to the tallest cell, so the fixed-height map left a dead
             * band under itself and each pair's shorter card grew empty space;
             * cards also had to be paired up, which broke whenever one of the
             * conditional cards (PermitCard, PSRDetailsCard) rendered null and
             * left a hole in the row.
             *
             * Each column is its own flex stack, so cards sit at their natural
             * height, follow the card above them immediately, and close up on
             * their own when a conditional card is absent — matching CORE.
             * Columns are assigned rather than auto-flowed (CSS multi-column)
             * because the Mapbox canvas needs a plainly-sized container and the
             * reading order must stay stable as content changes.
             */
            <div className="flex flex-col lg:flex-row lg:items-start gap-6">
              {/*
               * The column wrappers are `contents` until lg: on narrow screens they
               * generate no box, so the per-card wrappers become direct items of the
               * outer stack and their `order-*` interleaves the two columns into one
               * mobile reading order (Details, Customer, Map, Tasks, Permit, PSR,
               * Milestones). From lg they become real flex columns and `lg:order-none`
               * restores DOM order.
               *
               * `empty:hidden` on every card wrapper is what keeps the gaps honest:
               * PermitCard and PSRDetailsCard render null for most jobs, and an empty
               * wrapper would otherwise still count as a flex item and pull a gap-6
               * with it — reintroducing the dead space this layout removed.
               */}
              <div className="contents lg:flex lg:flex-col lg:gap-6 lg:min-w-0 lg:flex-1">
                <div className="order-1 lg:order-none empty:hidden">
                  <JobDetailsCard jobData={jobData} />
                </div>
                <div className="order-4 lg:order-none empty:hidden">
                  <TasksCard tasks={jobData.tasks} jobId={jobId} />
                </div>
                <div className="order-7 lg:order-none empty:hidden">
                  <MilestonesCard jobId={Number(jobId)} />
                </div>
              </div>

              <div className="contents lg:flex lg:flex-col lg:gap-6 lg:min-w-0 lg:flex-1">
                <div className="order-3 lg:order-none empty:hidden">
                  <MapSection address={jobData.address} coordinates={jobData.coordinates} />
                </div>
                <div className="order-2 lg:order-none empty:hidden">
                  <CustomerInfoCard
                    clientName={jobData.client}
                    contractName={jobData.contractName}
                    notes={typeof jobData.notes === 'string' ? jobData.notes : undefined}
                  />
                </div>
                <div className="order-5 lg:order-none empty:hidden">
                  <PermitCard jobData={jobData} />
                </div>
                <div className="order-6 lg:order-none empty:hidden">
                  <PSRDetailsCard psr={jobData.psr} />
                </div>
              </div>
            </div>
          )}

          {activeTab === 'activity-log' && <ActivityLogTab jobId={jobId} jobData={jobData} />}

          {activeTab === 'documents' && <DocumentsTab jobId={jobId} />}

          {activeTab === 'messages' &&
            (hasConversation ? (
              <ConversationThread
                conversationThread={conversationThread}
                jobStatus={jobStatus}
                jobId={Number(jobId)}
                showResponseForm={showResponseForm}
                setShowResponseForm={setShowResponseForm}
                responseMessage={responseMessage}
                setResponseMessage={setResponseMessage}
                onSendResponse={handleSendResponse}
              />
            ) : (
              <div className="max-w-4xl mx-auto">
                <div className="bg-card border border-border rounded-lg p-12 text-center">
                  <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center mx-auto mb-4">
                    <MessageSquare className="w-6 h-6 text-muted-foreground" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">No messages yet</h3>
                  <p className="text-sm text-muted-foreground">
                    Communication history will appear here when you request more information,
                    reject, or receive replies.
                  </p>
                </div>
              </div>
            ))}

          {activeTab === 'data-capture' && <DataCaptureTab jobId={jobId} />}

          {activeTab === 'dfe' && <DFETab jobId={jobId} />}
        </div>
      </div>
    </div>
  );
}
